import { SKTAutocompleteTextField } from "../components/SKTAutocompleteTextField";
import { SKTShipment } from "../components/SKTShipment";
import { SKTResults } from "../components/SKTResults";
import { SKTAction } from "../components/SKTAction";
import { SKTAddressField } from "../components/SKTAddressField";
import { SKTPayerField } from "../components/SKTPayerField";
import { SKTTextField } from "../components/SKTTextField";
import { SKTCheckbox } from "../components/SKTCheckbox";
import { OrderFlow } from "./OrderFlow";
import {
  isNumberInRange,
  passthru,
  isValidEmail,
  isValidName,
  isValidShortName,
  isValidContents,
  isValidAddress,
  isValidPhone,
  mustBeTrue,
} from "../utils/validators";
import {
  SKTPanelHeading,
  SKTPanel,
  SKTPanelBody,
} from "../components/SKTPanel";
import { apiGET } from "../hooks/APIHook";
import { SKTForm } from "../components/SKTForm";
import { SKTToggle } from "../components/SKTToggle";
import { API_ENDPOINTS } from "../config";
import { getStartDate, SKTDatePicker } from "../components/SKTDatePicker";
import Moment from "moment";
import { range } from "../utils/range";

const parcelFields = (count) => {
  try {
    return range(parseInt(count), 1).reduce((prev, index) => {
      const c = (key) => `${key}${index}`;
      return [
        ...prev,
        c("iPWeight"),
        c("iPDepth"),
        c("iPWidth"),
        c("iPHeight"),
      ];
    }, []);
  } catch (error) {
    return [];
  }
};

const palletFields = (count) => {
  try {
    return range(parseInt(count), 1).reduce((prev, index) => {
      const c = (key) => `${key}${index}`;
      return [...prev, c("iPWeight"), c("iPHeight"), c("iPPalletType")];
    }, []);
  } catch (error) {
    return [];
  }
};

const TavarakuljetuksetPhase1 = ({ data, options, moveToSecondPhase }) => {
  const requiredFields = () => {
    const routeFields = ["iSZIP", "iDZIP"];
    const dimensionFields = !!data.get("iPPallets")
      ? [...palletFields(data.get("iPParcel")), "iPParcel"]
      : [...parcelFields(data.get("iPParcel")), "iPParcel"];
    return [...routeFields, ...dimensionFields, "iPPallets", "on_top"];
  };
  console.log(requiredFields());
  const zipOptions = "FI";
  return (
    <SKTPanel>
      <SKTPanelBody>
        <SKTForm>
          <SKTAutocompleteTextField
            data={data}
            id="iSZIP"
            label="Noutopaikka"
            placeholder="00100 HELSINKI"
            options={zipOptions}
            showFlags={options.includeSE}
          />
          <SKTAutocompleteTextField
            data={data}
            id="iDZIP"
            label="Määräpaikka"
            placeholder="00100 HELSINKI"
            options={zipOptions}
            showFlags={options.includeSE}
            includeSE={options.includeSE}
          />
          <SKTShipment data={data} />
        </SKTForm>

        <SKTResults
          url={options.url}
          data={data}
          requiredFields={requiredFields}
          additionalFields={{ iDFirst: "1" }}
        />
        <SKTAction
          onClick={moveToSecondPhase}
          actionLabel="Jatka tilausta"
          active={true}
        >
          Seuraavassa vaiheessa kysymme hieman tarkempia tietoja kuljetukseesi
          liittyen. Tämän jälkeen siirrytään kuljetuksen maksuun. Hyväksymme
          yleisimmät suomalaiset verkkopankit ja luottokortit.
        </SKTAction>
      </SKTPanelBody>
    </SKTPanel>
  );
};

const tavarakuljetusRequiredFields = (data) => {
  const routeFields = [
    "iSZIP",
    "iSName",
    "iSAddr1",
    "iSPhone",
    "iSEmail",
    "iDZIP",
    "iDName",
    "iDAddr1",
    "iDPhone",
  ];
  const dimensionFields = !!data.get("iPPallets")
    ? [...palletFields(data.get("iPParcel")), "iPParcel"]
    : [...parcelFields(data.get("iPParcel")), "iPParcel"];
  return [...routeFields, ...dimensionFields, "iPPallets", "on_top"];
};

const TavarakuljetuksetPhase2 = ({ options, data, auth, moveToOrderPhase }) => {
  const orderValidFields = () => {
    const result = tavarakuljetusRequiredFields(data);
    if (!auth.auth) {
      return [...result, "iZEmail", "iZFirstName", "iZLastName"];
    }
    return result;
  };

  const hasValidDataForOrder = data.hasValidData(orderValidFields());

  const priceRequiredFields = () => {
    const routeFields = ["iSZIP", "iDZIP"];
    const dimensionFields = !!data.get("iPPallets")
      ? [...palletFields(data.get("iPParcel")), "iPParcel"]
      : [...parcelFields(data.get("iPParcel")), "iPParcel"];
    return [...routeFields, ...dimensionFields, "iPPallets", "on_top"];
  };

  return (
    <SKTPanel>
      <SKTPanelBody>
        <SKTForm>
          <SKTAddressField
            corporate={true}
            showFavorite={auth.auth}
            label="Noutopaikan tiedot"
            data={data}
            prefix="iS"
            showFlags={options.includeSE}
          />
          <SKTCheckbox
            data={data}
            label="Nouto nosturiautolla (maksullinen lisäpalvelu)"
            id="iPHIAB"
          />

          <SKTAddressField
            corporate={true}
            showFavorite={auth.auth}
            label="Määräpaikan tiedot"
            data={data}
            prefix="iD"
            hideEmail={true}
            showFlags={options.includeSE}
            includeSE={options.includeSE}
          />
          <SKTCheckbox
            data={data}
            label="Jakelu nosturiautolla (maksullinen lisäpalvelu)"
            id="iDHIAB"
          />
          <SKTToggle when={!auth.auth}>
            <SKTPayerField data={data} />
          </SKTToggle>
          <SKTShipment data={data} />
          <SKTTextField
            data={data}
            id="iPContents"
            label="Sisältö"
            placeholder="Kirjoita kuljetuksen sisältö tähän"
          />
          <SKTDatePicker
            data={data}
            id="iPBooking"
            label="Noutopäivä"
            placeholder="Haluttu noutopäivä muodossa pp.kk.vvvv"
          />
          <SKTToggle when={data.get("mode") === "corporate"}>
            <SKTCheckbox
              data={data}
              label="Jakelu yksityisille tai yrityksiin joissa ei ole kiinteällä aukiololla olevaa toimipistettä. Valittava yrityksille jotka toimivat YEH tai MTY tunnuksella. (maksullinen lisäpalvelu)"
              id="iPDelivery"
            />
          </SKTToggle>
          <SKTCheckbox
            data={data}
            label="Ilmoitus jakelusta 1h ennen (maksullinen lisäpalvelu)"
            id="iPNotifyDelivery"
          />
          <SKTToggle when={!auth.auth}>
            <SKTTextField
              data={data}
              id="iPYourCode"
              label="Maksajan viite/lisätieto"
              placeholder="esim. tilaajan nimi, tulosyksikkö, projektinumero"
            />
          </SKTToggle>

          <SKTTextField
            data={data}
            id="iPInstr"
            label="Ohjeet kuljettajalle"
            placeholder="Ohjeita kuljettajalle (max 120 merkkiä)"
          />
          <SKTCheckbox data={data} label="" id="accept_tc">
            Hyväksyn{" "}
            <a target="_new" href="/kayttoehdot/">
              palvelun käyttöehdot
            </a>
          </SKTCheckbox>
        </SKTForm>

        <SKTResults
          url={options.url}
          data={data}
          requiredFields={priceRequiredFields}
        />
        <SKTAction
          onClick={moveToOrderPhase}
          actionLabel="Jatka tilausta"
          active={hasValidDataForOrder}
        >
          Seuraavassa vaiheessa siirrytään kuljetuksen maksuun. Hyväksymme
          yleisimmät suomalaiset verkkopankit ja luottokortit.
        </SKTAction>
      </SKTPanelBody>
    </SKTPanel>
  );
};

const Tavarakuljetukset = ({ skt }) => {
  const options = {
    url: skt.priceurl ?? API_ENDPOINTS.priceQuery.parcel,
    includeSE: skt.se === "yes",
  };

  const variableValidators = range(20, 1).reduce((prev, index) => {
    const c = (key) => `${key}${index}`;
    prev[c("iPWeight")] = isNumberInRange(1, 22000);
    prev[c("iPWidth")] = isNumberInRange(1, 240);
    prev[c("iPHeight")] = isNumberInRange(1, 240);
    prev[c("iPDepth")] = isNumberInRange(1, 1000);
    prev[c("iPalletType")] = passthru;
    return prev;
  }, {});

  const corporateValidator = (value, values) => {
    if (skt.mode !== "corporate") return true;
    if (!values["iSName2"] && !values["iDName2"])
      return [false, "Vähintään nouto- tai toimitusosoitteen on oltava yritys"];
    return true;
  };

  const validators = {
    ...variableValidators,
    mode: passthru,

    iDZIP: passthru,

    iPWeight: isNumberInRange(1, 22000),
    iPWidth: isNumberInRange(1, 240),
    iPHeight: isNumberInRange(1, 240),
    iPDepth: isNumberInRange(1, 1000),
    iPParcel: isNumberInRange(1, 20),
    iPFIN: isNumberInRange(0, 20),
    iPTEHO: isNumberInRange(0, 20),
    iPEUR: isNumberInRange(0, 20),
    iPContents: isValidContents,

    iSZIP: passthru,
    iSName: isValidName,
    iSPhone: isValidPhone,
    iSEmail: isValidEmail,
    iSAddr1: isValidAddress,

    iDEmail: passthru,
    iZEmail: isValidEmail,
    iDName: isValidName,
    iDAddr1: isValidAddress,
    iDPhone: isValidPhone,
    iZFirstName: isValidShortName,
    iZLastName: isValidShortName,
    iSName2: corporateValidator,
    iDName2: corporateValidator,
    accept_tc: mustBeTrue,
  };
  console.log(validators);
  const createOrder = (data) => {
    const accepted = data.get("accept_tc");
    if (!accepted) {
      alert("Hyväksy palvelun ehdot");
      return;
    }
    const params = data.getQueryData();

    return apiGET(API_ENDPOINTS.orderCreation.parcel, { params }).then(
      (result) => {
        if (result.data.secret !== undefined) return result.data.secret;
        throw new Error(result.data.stack);
      }
    );
  };

  const defaults = {
    mode: skt.mode ?? "private",
    iPParcel: "1",
    iPFIN: "0",
    iPTEHO: "0",
    iPEUR: "0",
    iPBooking: getStartDate().format("D.M.Y"),
  };

  return (
    <OrderFlow
      phase1={TavarakuljetuksetPhase1}
      phase2={TavarakuljetuksetPhase2}
      createOrder={createOrder}
      validators={validators}
      defaults={defaults}
      options={options}
    />
  );
};

export default Tavarakuljetukset;
